import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, X, AlertTriangle, CheckCircle, Filter, Wallet, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';
import { PasteableUpload } from '@/components/common/PasteableUpload';
import { AuthedImage } from '@/components/common/AuthedImage';
import { VehiclePicker, type VehicleOption } from '@/components/common/VehiclePicker';
import { DriverPicker } from '@/components/common/DriverPicker';
import { useAuth } from '@/lib/auth';
import { useRef } from 'react';
import { RowDeleteButton } from '@/components/common/RowDeleteButton';
import { groupByPartition, fmtShort } from '@/lib/datePartition';
import { DriverSelect, toDriverOption } from '@/components/common/DriverSelect';

// PR-RIYADH-FIX-3 MAY 12 — Driver expense list + create drawer. Ties an
// expense to the driver's open advance so the running net balance picks it
// up. Approvals are restricted server-side; the UI just hides Approve/Reject
// from drivers.

const CATEGORIES = ['fuel', 'parts', 'tolls', 'parking', 'maintenance', 'food', 'other'] as const;
type Category = typeof CATEGORIES[number];

const STATUS_LABEL: Record<string, { text: string; cls: string }> = {
  submitted: { text: 'Pending', cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30' },
  approved:  { text: 'Approved', cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30' },
  rejected:  { text: 'Rejected', cls: 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30' },
};

interface ExpenseRow {
  id: string; code: string;
  driverId: string; driverCode: string; driverName: string;
  vehicleId: string | null; vehicleCode: string | null; vehiclePlate: string | null;
  expenseDate: string; category: Category;
  amountSar: number;
  status: 'submitted' | 'approved' | 'rejected';
  advanceCode: string | null;
  invoicePhotoUrl: string | null;
  vendorName: string | null;
  notes: string | null;
  createdByName: string | null;
}

interface DriverLite { id: string; code?: string | null; fullNameEn: string | null }
interface VehicleLite { id: string; code?: string | null; plateEn: string | null }
interface AdvanceLite {
  id: string; code: string; advanceDate: string; amountSar: number; remainingSar: number;
}

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

export default function DriverExpensesPage() {
  const { user } = useAuth();
  const role = user?.role ?? '';
  const canApprove = ['super_admin', 'admin', 'accountant'].includes(role);

  const [items, setItems] = useState<ExpenseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [driverFilter, setDriverFilter] = useState<string>('');
  const [vehicleFilter, setVehicleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'' | 'submitted' | 'approved' | 'rejected'>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const [drivers, setDrivers] = useState<DriverLite[]>([]);
  const [vehicles, setVehicles] = useState<VehicleLite[]>([]);

  const [showCreate, setShowCreate] = useState(false);
  const [actioning, setActioning] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (driverFilter) params.set('driverId', driverFilter);
      if (vehicleFilter) params.set('vehicleId', vehicleFilter);
      if (statusFilter)  params.set('status', statusFilter);
      if (fromDate)      params.set('from', fromDate);
      if (toDate)        params.set('to', toDate);
      const r = await apiClient.get<{ items: ExpenseRow[] }>('/api/v1/driver-expenses?' + params.toString());
      setItems(r.data.items ?? []);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, [driverFilter, vehicleFilter, statusFilter, fromDate, toDate]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    apiClient.get<{ items?: DriverLite[] }>('/api/v1/drivers?status=active&pageSize=300')
      .then((r) => setDrivers(r.data.items ?? []))
      .catch(() => setDrivers([]));
    apiClient.get<{ items?: VehicleLite[] }>('/api/v1/vehicles?status=active&pageSize=300')
      .then((r) => setVehicles(r.data.items ?? []))
      .catch(() => setVehicles([]));
  }, []);

  async function action(id: string, kind: 'approved' | 'rejected') {
    setActioning(id);
    try {
      const body: Record<string, unknown> = { status: kind };
      if (kind === 'rejected') {
        const reason = window.prompt('Rejection reason?');
        if (!reason) { setActioning(null); return; }
        body.rejectedReason = reason;
      }
      await apiClient.patch('/api/v1/driver-expenses/' + id, body);
      await refresh();
    } catch (e) {
      window.alert(extractErrorMessage(e));
    } finally {
      setActioning(null);
    }
  }

  const total = useMemo(() => items.reduce((acc, r) => acc + r.amountSar, 0), [items]);
  const approved = useMemo(() => items.filter((r) => r.status === 'approved').reduce((acc, r) => acc + r.amountSar, 0), [items]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Home</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Receipt className="h-4 w-4" /> Driver expenses</div>
          <Button onClick={() => setShowCreate(true)} className="gap-2" size="sm">
            <Plus className="h-4 w-4" /> New expense
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Stat label="Rows shown" value={items.length.toString()} />
          <Stat label="Total submitted (SAR)" value={total.toLocaleString(undefined, { minimumFractionDigits: 2 })} />
          <Stat label="Total approved (SAR)" value={approved.toLocaleString(undefined, { minimumFractionDigits: 2 })} />
        </div>

        <div className="rounded-lg border border-border p-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-1.5">
            <Filter className="h-3 w-3" /> Filters
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
            <Field label="Driver">
              <DriverSelect
                value={driverFilter}
                onChange={setDriverFilter}
                drivers={drivers.map(toDriverOption)}
                noneLabel="— Any —"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              />
            </Field>
            <Field label="Vehicle">
              <select value={vehicleFilter} onChange={(e) => setVehicleFilter(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="">— Any —</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plateEn ?? v.code ?? v.id.slice(0, 8)}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="">— Any —</option>
                <option value="submitted">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </Field>
            <Field label="From">
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </Field>
            <Field label="To">
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </Field>
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {error}
          </div>
        )}

        <div className="rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Driver</th>
                  <th className="text-left px-3 py-2">Vehicle</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Advance</th>
                  <th className="text-left px-3 py-2">Status</th>
                  {canApprove && <th className="text-right px-3 py-2">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={canApprove ? 9 : 8} className="px-3 py-6 text-center text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin inline" /> Loading…
                  </td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={canApprove ? 9 : 8} className="px-3 py-6 text-center text-muted-foreground">No expenses match these filters.</td></tr>
                ) : groupByPartition(items, (r) => r.expenseDate).map((g) => (
                  <>
                    <tr key={`hdr-${g.partition}`} className="bg-muted/10">
                      <td colSpan={canApprove ? 9 : 8} className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                        {g.label} <span className="text-muted-foreground/70 normal-case">({g.items.length})</span>
                      </td>
                    </tr>
                    {g.items.map((r) => {
                      const s = STATUS_LABEL[r.status] ?? STATUS_LABEL.submitted;
                      return (
                        <tr key={r.id} className="border-t border-border hover:bg-muted/20">
                          <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{fmtShort(r.expenseDate)}</td>
                          <td className="px-3 py-2">{r.driverName} <span className="text-xs text-muted-foreground font-mono">{r.driverCode}</span></td>
                          <td className="px-3 py-2">{r.vehiclePlate ?? r.vehicleCode ?? '—'}</td>
                          <td className="px-3 py-2 capitalize">{r.category}</td>
                          <td className="px-3 py-2 text-right font-mono">{r.amountSar.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                          <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{r.advanceCode ?? '—'}</td>
                          <td className="px-3 py-2">
                            <span className={'text-[11px] px-2 py-0.5 rounded-full border ' + s.cls}>{s.text}</span>
                          </td>
                          {canApprove && (
                            <td className="px-3 py-2 text-right">
                              <div className="inline-flex gap-1 items-center">
                                {r.status === 'submitted' && (
                                  <>
                                    <Button size="sm" variant="outline" disabled={actioning === r.id} onClick={() => action(r.id, 'approved')}>
                                      {actioning === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Approve'}
                                    </Button>
                                    <Button size="sm" variant="outline" disabled={actioning === r.id} onClick={() => action(r.id, 'rejected')}>Reject</Button>
                                  </>
                                )}
                                <RowDeleteButton
                                  endpoint={`/api/v1/driver-expenses/${r.id}`}
                                  entityKind="expense"
                                  label={r.code ?? r.id}
                                  onDeleted={refresh}
                                />
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {showCreate && (
        <NewExpenseDrawer
          onClose={() => setShowCreate(false)}
          onSaved={async () => { setShowCreate(false); await refresh(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold mt-0.5 font-mono">{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function NewExpenseDrawer({
  onClose, onSaved,
}: {
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [driverId, setDriverId] = useState<string | null>(null);
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  // PR-DROPDOWN-SCOPE MAY 12 — when the user explicitly picks a driver, latch
  // the manual flag so subsequent vehicle changes don't blow their choice
  // away. Resets to false when the user clears the vehicle.
  const driverManualRef = useRef(false);
  // The vehicle's current driver, used for the "Recommended" hint + auto-fill.
  const [recommendedDriverId, setRecommendedDriverId] = useState<string | null>(null);
  const [autoFilled, setAutoFilled] = useState(false);

  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().slice(0, 10));
  const [category, setCategory] = useState<Category>('fuel');
  const [amountSar, setAmountSar] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [reduceFromAdvance, setReduceFromAdvance] = useState(true);
  const [advances, setAdvances] = useState<AdvanceLite[]>([]);
  const [advanceId, setAdvanceId] = useState<string>('');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!driverId) { setAdvances([]); setAdvanceId(''); return; }
    apiClient.get<{ recentAdvances: AdvanceLite[] }>('/api/v1/drivers/' + driverId + '/balance')
      .then((r) => {
        const open = (r.data.recentAdvances ?? []).filter((a) => a.remainingSar > 0);
        setAdvances(open);
        setAdvanceId(open[0]?.id ?? '');
      })
      .catch(() => setAdvances([]));
  }, [driverId]);

  const selectedAdvance = advances.find((a) => a.id === advanceId) ?? null;

  function onVehiclePicked(id: string | null, v: VehicleOption | null) {
    setVehicleId(id);
    const cdId = v?.currentDriver?.id ?? null;
    setRecommendedDriverId(cdId);
    if (!id) {
      // Cleared vehicle → reset the manual latch so the next pick can auto-fill again.
      driverManualRef.current = false;
      setAutoFilled(false);
      return;
    }
    // Auto-fill driver IF user hasn't manually overridden it.
    if (!driverManualRef.current && cdId && cdId !== driverId) {
      setDriverId(cdId);
      setAutoFilled(true);
    }
  }

  function onDriverPicked(id: string | null) {
    setDriverId(id);
    driverManualRef.current = id !== null;
    if (id !== recommendedDriverId) setAutoFilled(false);
  }

  // Real upload to the server-side stage-invoice endpoint. The endpoint
  // returns a server-relative URL; we keep that URL in state and the create
  // call persists it as invoice_photo_url. AuthedImage renders the thumbnail.
  async function uploadPhoto(file: File) {
    setError(null);
    const fd = new FormData();
    fd.append('file', file);
    const jwt = getStoredToken();
    const res = await fetch(API_BASE + '/api/v1/driver-expenses/stage-invoice', {
      method: 'POST',
      headers: jwt ? { Authorization: 'Bearer ' + jwt } : {},
      body: fd,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as { message?: string; error?: string }).message
        ?? (body as { error?: string }).error
        ?? `HTTP ${res.status}`);
    }
    const json = await res.json() as { url: string };
    setPhotoUrl(json.url);
  }

  async function submit() {
    setError(null);
    if (!driverId) { setError('Driver required'); return; }
    if (!amountSar) { setError('Amount required'); return; }
    setSubmitting(true);
    try {
      await apiClient.post('/api/v1/driver-expenses', {
        driverId,
        vehicleId: vehicleId ?? undefined,
        expenseDate,
        category,
        amountSar: Number(amountSar),
        vendorName: vendorName.trim() || undefined,
        invoiceNumber: invoiceNumber.trim() || undefined,
        invoicePhotoUrl: photoUrl ?? undefined,
        paidFromAdvanceId: reduceFromAdvance && advanceId ? advanceId : undefined,
        isCompanyExpense: true,
        notes: notes.trim() || undefined,
      });
      await onSaved();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="font-medium inline-flex items-center gap-2"><Plus className="h-4 w-4" /> New driver expense</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* PR-DROPDOWN-SCOPE: vehicle FIRST so picking it auto-fills the driver below. */}
          <Field label="Vehicle (optional)">
            <VehiclePicker
              value={vehicleId}
              onChange={onVehiclePicked}
              placeholder="— No vehicle —"
            />
          </Field>
          <Field label={
            <span className="inline-flex items-center gap-2">
              Driver (required)
              {autoFilled && (
                <span className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                  auto-selected · click to change
                </span>
              )}
            </span>
          }>
            <DriverPicker
              value={driverId}
              onChange={(id) => onDriverPicked(id)}
              preferredDriverId={recommendedDriverId}
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Expense date">
              <Input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} />
            </Field>
            <Field label="Category">
              <select value={category} onChange={(e) => setCategory(e.target.value as Category)} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base capitalize">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Amount (SAR)">
              <Input type="number" step="0.01" min={0} value={amountSar} onChange={(e) => setAmountSar(e.target.value)} placeholder="150.00" className="font-mono" />
            </Field>
            <Field label="Vendor (Shell, ADNOC, …)">
              <Input value={vendorName} onChange={(e) => setVendorName(e.target.value)} />
            </Field>
            <Field label="Invoice number">
              <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
            </Field>
          </div>

          <Field label="Invoice photo">
            <PasteableUpload
              currentFileUrl={photoUrl}
              onUpload={async (f) => { await uploadPhoto(f); }}
              onReplace={() => setPhotoUrl(null)}
              maxSizeMB={10}
            />
            {photoUrl && photoUrl.startsWith('/api/v1/driver-expenses/invoice/') && (
              <div className="mt-2">
                <AuthedImage
                  src={photoUrl}
                  alt="Invoice preview"
                  className="w-24 h-24 rounded border border-border object-cover"
                />
              </div>
            )}
          </Field>

          <div className="rounded-md border border-border p-3 space-y-2 bg-muted/10">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={reduceFromAdvance} onChange={(e) => setReduceFromAdvance(e.target.checked)} />
              <Wallet className="h-3.5 w-3.5" /> Reduce from driver's advance (recommended)
            </label>
            {reduceFromAdvance && (
              <div>
                {advances.length === 0 ? (
                  <div className="text-xs text-muted-foreground">No open advances for this driver — record without linking.</div>
                ) : (
                  <Field label="Advance">
                    <select value={advanceId} onChange={(e) => setAdvanceId(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                      {advances.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} · {a.amountSar.toFixed(2)} ({a.remainingSar.toFixed(2)} remaining · {a.advanceDate})
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
                {selectedAdvance && amountSar && (
                  <div className="text-xs text-muted-foreground mt-1">
                    After this expense: <strong className="text-foreground">
                      {(selectedAdvance.remainingSar - Number(amountSar)).toFixed(2)} SAR
                    </strong> remaining on {selectedAdvance.code}.
                  </div>
                )}
              </div>
            )}
          </div>

          <Field label="Notes">
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base" />
          </Field>

          {error && (
            <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-2 text-sm inline-flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> {error}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-border flex justify-end gap-2 bg-muted/20">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !driverId || !amountSar} className="gap-2">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            Submit for approval
          </Button>
        </div>
      </div>
    </div>
  );
}
